import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ClipboardCheck, PackageSearch, ShieldCheck, Wrench, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import * as scheduleApi from "../../api/schedule";
import * as dashboardApi from "../../api/dashboard";
import * as interventionsApi from "../../api/interventions";
import { RiepilogoModuli } from "../../components/ui/RiepilogoModuli";
import { DonutPerStato } from "../../components/ui/DonutPerStato";
import { InstallazioniPerStato } from "../../components/ui/InstallazioniPerStato";
import { SectionKey, sectionTileStyle } from "../../utils/sectionColors";
import { DaFareCard } from "./DaFareCard";

// Le azioni rapide aprono direttamente il form di creazione del modulo (?new=1),
// non la sola lista. La visibilità segue il PERMESSO di creazione, non una lista
// di ruoli hardcodata (stessa scelta già fatta per menu, route guard e card
// contatori): così vale anche per gli override per-utente della Fase 2.4.
const QUICK_ACTIONS: {
  to: string;
  label: string;
  icon: typeof ClipboardCheck;
  section: SectionKey;
  permission: string;
}[] = [
  { to: "/sopralluoghi?new=1", label: "Nuovo Sopralluogo", icon: ClipboardCheck, section: "sopralluoghi", permission: "sopralluoghi:create" },
  { to: "/interventions?new=1", label: "Nuovo Intervento", icon: Wrench, section: "interventi", permission: "interventi:create" },
  { to: "/schedule?new=1", label: "Nuova Installazione", icon: PackageSearch, section: "installazioni", permission: "installazioni:create" },
  { to: "/activations?new=1", label: "Nuova Attivazione", icon: ShieldCheck, section: "attivazioni", permission: "attivazioni:create" },
];

// Come per le card del Riepilogo: i sotto-conteggi seguono il permesso di
// lettura del modulo, perché ognuno è un link alla lista già filtrata. Il
// totale in evidenza NON cambia — lo calcola il backend sullo scope, e un
// modulo che l'utente non può leggere ci contribuisce con zero.
const RITARDO_MODULI: {
  key: "installazioni" | "sopralluoghi" | "attivazioni" | "interventi";
  label: string;
  to: string;
  permission: string;
}[] = [
  { key: "installazioni", label: "Installazioni", to: "/schedule?ritardo=true", permission: "installazioni:read" },
  { key: "sopralluoghi", label: "Sopralluoghi", to: "/sopralluoghi?ritardo=true", permission: "sopralluoghi:read" },
  { key: "attivazioni", label: "Attivazioni", to: "/activations?ritardo=true", permission: "attivazioni:read" },
  { key: "interventi", label: "Interventi", to: "/interventions?ritardo=true", permission: "interventi:read" },
];

/**
 * Riquadro "Ritardi": totale in evidenza + quattro sotto-conteggi cliccabili
 * per modulo, che aprono la lista con ?ritardo=true già applicato. Se il totale
 * è zero mostra uno stato neutro invece di sparire, per confermare a colpo
 * d'occhio che è tutto in linea.
 */
function RitardiCard({ ritardi, isLoading }: { ritardi: scheduleApi.RitardiStats["ritardi"] | null; isLoading: boolean }) {
  const { hasPermission } = useAuth();
  const moduli = RITARDO_MODULI.filter((m) => hasPermission(m.permission));
  if (isLoading) {
    return <div className="mb-8 h-24 animate-pulse rounded-card border border-line bg-white/60" />;
  }
  const totale = ritardi?.totale ?? 0;

  return (
    <section className="mb-8">
      <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">Ritardi</h2>
      {totale === 0 ? (
        <div className="flex items-center gap-2 rounded-card border border-line bg-white p-4 text-sm text-ink/60 shadow-card">
          <CheckCircle2 className="h-5 w-5 text-[#16a34a]" aria-hidden="true" />
          Nessun ritardo — tutto in linea.
        </div>
      ) : (
        <div className="rounded-card border border-line border-l-4 border-l-[#ef4444] bg-white p-4 shadow-card">
          <div className="mb-3 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-[#dc2626]" aria-hidden="true" />
            <span className="text-2xl font-bold text-[#dc2626]">{totale}</span>
            <span className="text-sm text-ink/60">{totale === 1 ? "record in ritardo" : "record in ritardo"}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {moduli.map(({ key, label, to }) => {
              const n = ritardi?.per_modulo[key] ?? 0;
              return (
                <Link
                  key={key}
                  to={to}
                  className={`flex flex-col rounded-lg border px-3 py-2 transition-colors ${
                    n > 0 ? "border-[#ef4444]/30 bg-[#ef4444]/5 hover:bg-[#ef4444]/10" : "border-line hover:bg-black/5"
                  }`}
                >
                  <span className={`text-xl font-bold ${n > 0 ? "text-[#dc2626]" : "text-ink/40"}`}>{n}</span>
                  <span className="text-xs text-ink/60">{label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

export default function Dashboard() {
  const { user, hasPermission } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  // Drill-down dal donut degli Interventi verso la lista già filtrata. La
  // fetta "Senza stato" non può passare una stringa vuota (sarebbe come non
  // filtrare), quindi viaggia con la convenzione STATO_NULLI condivisa col
  // backend, che la traduce in `stato IS NULL`.
  function apriInterventiPerStato(stato: string) {
    const valore = stato === "Senza stato" ? interventionsApi.STATO_NULLI : stato;
    navigate(`/interventions?stato=${encodeURIComponent(valore)}`);
  }
  // Il donut porta alla lista solo per chi può leggerla: senza il permesso
  // resta informativo, invece di offrire un click che finirebbe su una pagina
  // negata dalla route guard.
  const drillInterventi = hasPermission("interventi:read") ? apriInterventiPerStato : undefined;

  const [stats, setStats] = useState<scheduleApi.ScheduleStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [ritardi, setRitardi] = useState<scheduleApi.RitardiStats["ritardi"] | null>(null);
  const [ritardiLoading, setRitardiLoading] = useState(true);
  // Riepilogo aggregato dei 4 moduli (conteggi, per-stato, andamento) —
  // già scopizzato lato server, il frontend non filtra nulla.
  const [riepilogo, setRiepilogo] = useState<dashboardApi.RiepilogoModuli | null>(null);
  const [riepilogoLoading, setRiepilogoLoading] = useState(true);

  // La card dei contatori segue il PERMESSO, non una lista di ruoli hardcodata
  // (stessa scelta già fatta per menu e route guard): così la vedono tutti quelli
  // che possono leggere le installazioni — admin/staff (tutti i record), CA/OT e
  // cliente (solo i propri, il backend scopizza /schedule/stats con
  // buildScopedWhere) — e gli override per-utente (Fase 2.4) valgono anche qui.
  const hasScheduleAccess = hasPermission("installazioni:read");
  // Chi non ha scoping (admin/staff) vede il totale globale; gli altri solo la
  // propria competenza: il testo dell'intestazione lo dice esplicitamente.
  const vedeTuttiIRecord = user?.role === "admin" || user?.role === "staff";
  const visibleActions = QUICK_ACTIONS.filter((a) => hasPermission(a.permission));

  useEffect(() => {
    if (!hasScheduleAccess) {
      setIsLoading(false);
      return;
    }

    scheduleApi
      .getScheduleStats()
      .then(setStats)
      .catch((err) => showToast(getErrorMessage(err, "Impossibile caricare i contatori."), "error"))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasScheduleAccess]);

  useEffect(() => {
    dashboardApi
      .getRiepilogo()
      .then(setRiepilogo)
      .catch((err) => showToast(getErrorMessage(err, "Impossibile caricare il riepilogo dei moduli."), "error"))
      .finally(() => setRiepilogoLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ritardi: visibili a tutti i ruoli (scopati lato server), indipendenti
  // dall'accesso al calendario installazioni.
  useEffect(() => {
    scheduleApi
      .getRitardi()
      .then((r) => setRitardi(r.ritardi))
      .catch((err) => showToast(getErrorMessage(err, "Impossibile caricare i ritardi."), "error"))
      .finally(() => setRitardiLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-ink">Ciao, {user?.full_name ?? user?.email}</h1>
        <p className="mt-1 text-sm text-ink/50">
          {!hasScheduleAccess
            ? "Panoramica della tua attività su Wink on Support."
            : vedeTuttiIRecord
              ? "Panoramica delle installazioni — tutti i record, senza filtro sul mese corrente."
              : "Panoramica delle installazioni di tua competenza — senza filtro sul mese corrente."}
        </p>
      </header>

      {visibleActions.length > 0 && (
        <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {visibleActions.map(({ to, label, icon: Icon, section }) => (
            <Link
              key={to}
              to={to}
              style={sectionTileStyle(section)}
              className="flex flex-col items-center gap-2 rounded-card px-4 py-5 text-center transition-transform hover:-translate-y-0.5 active:translate-y-0 active:shadow-none"
            >
              <Icon className="h-6 w-6" aria-hidden="true" />
              <span className="text-sm font-semibold">{label}</span>
            </Link>
          ))}
        </div>
      )}

      <RiepilogoModuli dati={riepilogo} isLoading={riepilogoLoading} />

      <RitardiCard ritardi={ritardi} isLoading={ritardiLoading} />

      {/* Ordini mancanti (issue #211): solo admin e staff. Convive con il badge
          «Attivazione da creare» delle installazioni, che resta com'è. */}
      {hasPermission("stato_lavori:read") && <DaFareCard />}

      {/* Installazioni per stato (tessere cliccabili + distribuzione + prossimi
          7 giorni) affiancate al donut degli Interventi, come nel mockup: le
          tessere restano la vista principale delle installazioni, il donut
          Interventi le sta accanto. Gli stati NON sono comparabili tra moduli
          (enumerazioni diverse, e gli Interventi ricevono valori liberi da
          Odoo), quindi ognuno ha la propria legenda. */}
      {hasScheduleAccess ? (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_320px] xl:items-start">
          <div className="min-w-0">
            <InstallazioniPerStato
              byStato={stats?.by_stato ?? {}}
              prossimi7Giorni={stats?.prossimi_7_giorni ?? []}
              isLoading={isLoading}
            />
          </div>
          {/* Stessa struttura della colonna a sinistra — titolo di sezione
              fuori, card sotto — perché è ciò che le allinea: InstallazioniPerStato
              apre con il proprio <h2>, quindi una card "nuda" qui partirebbe
              32px più in alto delle tessere (l'altezza esatta di quel titolo). */}
          <section className="min-w-0">
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">Interventi — per stato</h2>
            <DonutPerStato
              titolo="Distribuzione"
              perStato={riepilogo?.interventi.per_stato ?? {}}
              isLoading={riepilogoLoading}
              onSelezionaStato={drillInterventi}
            />
          </section>
        </div>
      ) : (
        <section>
          <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">Interventi — per stato</h2>
          <DonutPerStato
            titolo="Distribuzione"
            perStato={riepilogo?.interventi.per_stato ?? {}}
            isLoading={riepilogoLoading}
            onSelezionaStato={drillInterventi}
          />
        </section>
      )}
    </div>
  );
}

